/* <radar-model>: loads a glTF into model-viewer when the section comes
   near the viewport, repaints its materials in RADAR colours and keeps the
   poster image if anything fails. Without JavaScript only the poster shows. */
(function (global) {
  'use strict';

  var reducedMotion = global.matchMedia ? global.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  // model-viewer ships with the theme (assets/radar-model-viewer.min.js, Apache-2.0):
  // Shopify only injects its own copy on pages that carry Shopify-hosted 3D media.
  // The module is fetched once, the first time a section needs it.
  var pending = null;
  function loadViewer(src) {
    if (pending) return pending;
    pending = new Promise(function (resolve, reject) {
      if (global.customElements && global.customElements.get('model-viewer')) return resolve();
      if (!src) return reject(new Error('no viewer source'));
      var script = document.createElement('script');
      script.type = 'module';
      script.src = src;
      script.addEventListener('error', function () { reject(new Error('viewer failed to load')); });
      script.addEventListener('load', function () {
        if (global.customElements.get('model-viewer')) return resolve();
        global.customElements.whenDefined('model-viewer').then(resolve, reject);
      });
      document.head.appendChild(script);
    });
    return pending;
  }

  // Seams, edges and elastics keep the trim colour; the cloth itself takes the
  // fabric colour. Models that name a material "fabric" say so outright; the ones
  // that do not (the T-shirt calls its cloth "softec") are read the other way
  // round, or the whole piece would come out in the trim colour.
  var TRIM = /(seam|stitch|cucitur|bordin|elastic|zip|collar)/;

  function paint(viewer, fabric, trim) {
    var model = viewer.model;
    if (!model || !model.materials) return;
    var named = model.materials.some(function (material) {
      return (material.name || '').toLowerCase().indexOf('fabric') > -1;
    });
    model.materials.forEach(function (material) {
      var name = (material.name || '').toLowerCase();
      var isTrim = named ? name.indexOf('fabric') === -1 : TRIM.test(name);
      var colour = isTrim ? trim : fabric;
      if (!colour) return;
      try {
        material.pbrMetallicRoughness.setBaseColorFactor(colour);
      } catch (error) {
        /* a material we cannot repaint keeps the colour it came with */
      }
    });
  }

  var RadarModel = function () {};
  if (typeof HTMLElement === 'function') {
    RadarModel = class extends HTMLElement {
      connectedCallback() {
        if (this.started) return;
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.start();
          },
          { rootMargin: '300px' },
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        this.observer = null;
      }

      start() {
        if (this.started) return;
        this.started = true;
        if (this.observer) this.observer.disconnect();
        const src = this.dataset.model;
        if (!src) return;
        loadViewer(this.dataset.viewer)
          .then(() => this.mount(src))
          .catch(() => this.classList.add('is-failed'));
      }

      mount(src) {
        const viewer = document.createElement('model-viewer');
        viewer.setAttribute('src', src);
        viewer.setAttribute('alt', this.dataset.alt || '');
        viewer.setAttribute('camera-controls', '');
        viewer.setAttribute('touch-action', 'pan-y');
        viewer.setAttribute('disable-zoom', '');
        viewer.setAttribute('shadow-intensity', '1');
        viewer.setAttribute('exposure', '1');
        viewer.setAttribute('environment-image', 'neutral');
        // Framing only: min-camera-orbit/max-camera-orbit with "auto" for the
        // horizontal angle pin it to the starting value and the model stops
        // turning, so the framing is left to camera-orbit and disable-zoom.
        viewer.setAttribute('camera-orbit', '25deg 78deg 118%');
        viewer.setAttribute('loading', 'eager');
        viewer.className = 'radar-feature__viewer';
        if (!reducedMotion.matches) {
          viewer.setAttribute('auto-rotate', '');
          viewer.setAttribute('auto-rotate-delay', '1200');
          viewer.setAttribute('rotation-per-second', '18deg');
        }
        // The camera-orbit attribute is the camera's goal: left in place it pulls
        // the model back to the starting angle after every drag, so it goes as
        // soon as the visitor takes over.
        viewer.addEventListener('camera-change', (event) => {
          if (event.detail && event.detail.source === 'user-interaction') viewer.removeAttribute('camera-orbit');
        });
        // model-viewer drops an 8x8 "pan target" under the pointer, and it swallows
        // the drag that starts on it - the dead centre of the model, where anyone
        // grabs it. Filling the slot ourselves replaces it with nothing.
        const panTarget = document.createElement('div');
        panTarget.setAttribute('slot', 'pan-target');
        panTarget.style.pointerEvents = 'none';
        viewer.appendChild(panTarget);
        viewer.addEventListener('load', () => {
          paint(viewer, this.dataset.fabric, this.dataset.trim);
          this.classList.add('is-ready');
        });
        viewer.addEventListener('error', () => {
          this.classList.add('is-failed');
          if (viewer.parentNode) viewer.parentNode.removeChild(viewer);
        });
        this.insertBefore(viewer, this.firstChild);
      }
    };

    if (!customElements.get('radar-model')) customElements.define('radar-model', RadarModel);
  }

  global.RadarFeature = { loadViewer: loadViewer, paint: paint };
})(typeof window !== 'undefined' ? window : globalThis);
